import { AfterContentInit, Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { parseZone } from 'moment';
import { map } from 'rxjs';

import { GridColumn, GridColumnTypeEnum } from '../../../../models/common/grid-column.models';
import { SidebarTypeEnum } from '../../../../models/common/sidebar-type.enum';
import { CompanyOrderListItem } from 'src/app/components/company/models/company-order-list-item.type';
import { SidenavService } from '../../../../shared/services/sidenav.service';
import { TopbarService } from '../../../../shared/services/topbar.service';
import { UtilityFunctions } from '../../../../shared/utilities/functions.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterDialogData } from '../../../../shared/models/types/filter-dialog-data.type';
import { PhotographerFilterService } from 'src/app/shared/services/filters/photographer-filter.service';
import { GraphQLService } from '../../../../services/graph-ql.service';
import { OrderStatus } from '../../../../models/common/order-status.models';
import { OrderStatusFilterService } from 'src/app/shared/services/filters/order-status-filter.service';
import { MatDialog } from '@angular/material/dialog';
import { FilterListComponent } from '../../../../shared/components/filter-list/filter-list.component';
import { FilterService } from '../../../../shared/services/filter.service';
import { OrderQueries, PhotographerQueries } from '../../../../models/shared/enums/get-query';
import { AppService } from '../../../../app.service';
import { WrapperResultGraphQL } from '../../../../models/shared/types/wrapper-result-graphql.type';
import { Photographer } from '../../../../models/common/photographer.models';
import { Ids } from '../../../../models/common/ids.model';
import { MutationQuery } from 'src/app/models/shared/enums/mutation-query.enum';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import { DateFilterListComponent } from 'src/app/shared/components/date-filter-list/date-filter-list.component';
import { IdsFilterListComponent } from 'src/app/shared/components/ids-filter-list/ids-filter-list.component';

@Component({
	standalone: false,
	selector: 'bt-company-order-list',
	templateUrl: './company-order-list.component.html',
	styleUrls: ['./company-order-list.component.scss']
})
export class CompanyOrderListComponent implements AfterContentInit {
	readonly #destroyRef = inject(DestroyRef);
	readonly #graphQlService = inject(GraphQLService);
	readonly #dialog = inject(MatDialog);
	readonly #filterService = inject(FilterService);
	readonly #appService = inject(AppService);
	readonly #activatedRoute = inject(ActivatedRoute);
	readonly #topbarService = inject(TopbarService);
	readonly #sidenavService = inject(SidenavService);
	readonly #router = inject(Router);
	readonly #utilitiesService = inject(UtilitiesService);

	currentSidebar: SidebarTypeEnum;
	sidebarTypeEnum = SidebarTypeEnum;
	gridColumnTypeEnum = GridColumnTypeEnum;
	gridColumns: Array<GridColumn>;

	orders: { data: Array<CompanyOrderListItem>; page: number; search: string; size: number; total: number } = {
		size: 10,
		page: 0,
		search: '',
		data: [],
		total: 0
	};
	loaderId = UtilityFunctions.newGuid();

	ids = {
		title: 'Order ID',
		selected: []
	};

	filtersList = [
		{ id: 'photographers', label: 'Photographers' },
		{ id: 'orderStatuses', label: 'Order Status' }
	];

	photographers: FilterDialogData<Photographer> = {
		title: 'Photographers',
		selected: [],
		filterService: inject(PhotographerFilterService),
		variables: { page: 0, size: 50 }
	};
	orderStatuses: FilterDialogData<OrderStatus> = {
		title: 'Order Status',
		selected: [],
		filterService: inject(OrderStatusFilterService)
	};

	dateFiltersList = [
		{ id: 'dueDate', label: 'Due Date' },
		{ id: 'creationDate', label: 'Creation Date' },
		{ id: 'completitionDate', label: 'Completition Date' }
	];

	dueDate = {
		title: 'Due Date',
		startDate: null,
		endDate: null
	};
	creationDate = {
		title: 'Creation Date',
		startDate: null,
		endDate: null
	};
	completitionDate = {
		title: 'Completition Date',
		startDate: null,
		endDate: null
	};

	constructor() {
		this.queryParamsSubscribe();
	}

	ngAfterContentInit(): void {
		//Acquisizione dei parametri dell'url
		this.getData();
	}

	onCustomOrder() {
		this.#router.navigate(['/company/new-order/order-detail-info']);
	}

	onInfo(item: CompanyOrderListItem) {
		this.#router.navigate(['/company/order-info/order-detail-info', item.orderId]);
	}

	openDialogIdsFilter(): void {
		const dialog = this.#dialog.open(IdsFilterListComponent, {
			data: { title: this.ids.title, selected: this.ids.selected }
		});

		dialog.afterClosed().subscribe({
			next: (res) => {
				if (res) {
					this.ids.selected = res;
					const ids = res.map((el) => `${el}`).join('-');
					this.#filterService.setQueryParams({ ids: ids });
				}
			}
		});
	}

	openDialogFilter(title: string = ''): void {
		const dialog = this.#dialog.open(FilterListComponent<any>, {
			data: { title: this[title].title, selected: this[title].selected, filterService: this[title].filterService, variables: this[title].variables },
			width: '400px'
		});

		dialog.afterClosed().subscribe({
			next: (res) => {
				if (res) {
					this.applyFilters(title, res);
				}
			}
		});
	}

	openDialogDateFilter(title: string = ''): void {
		const dialog = this.#dialog.open(DateFilterListComponent, {
			data: { title: this[title].title, startDate: this[title].startDate, endDate: this[title].endDate }
		});

		dialog.afterClosed().subscribe({
			next: (res) => {
				if (res) {
					this[title].startDate = res.startDate;
					this[title].endDate = res.endDate;
					this.applyFilters(title, []);
				}
			}
		});
	}

	getLabelIds(): string {
		return this.ids.selected.length
			? `: ${this.ids.selected
					.map((el) => `${el}`)
					.join(', ')
					.slice(0, 20)}`
			: '';
	}

	getLabel(filter: string): string {
		return this[filter].selected.length
			? `: ${this[filter].selected
					.map((el) => el.displayName)
					.join(', ')
					.slice(0, 20)}`
			: '';
	}

	getLabelDate(filter: string): string {
		let label = '';
		this[filter].startDate ? (label += ` from ${this[filter].startDate.toLocaleDateString()}`) : '';
		this[filter].endDate ? (label += ` to ${this[filter].endDate.toLocaleDateString()}`) : '';
		return label;
	}

	getFullTitleFilter(filter: string): string {
		return this[filter].selected.length ? `${this[filter].selected.map((el) => el.displayName).join(', ')}` : '';
	}

	clearFilter(event: any, filter: string): void {
		event.stopPropagation();
		this[filter].selected = [];
		(this[filter] as FilterDialogData<any>).filterService.clearSelection();
		this.#filterService.removeDataFromUrl(filter, () => null);
		if (filter === 'brands') this['categories'].variables.brandIds = [];
	}

	clearIdsFilter(event: any): void {
		event.stopPropagation();
		this.ids.selected = [];

		this.#filterService.removeDataFromUrl('ids', () => null);
	}

	clearDateFilter(event: any, filter: string): void {
		event.stopPropagation();
		this[filter].startDate = null;
		this[filter].endDate = null;

		this.#filterService.removeDataFromUrl(filter, () => null);
	}

	getData() {
		const params = this.#filterService.getDataFromUrl();
		this.getParams(params);
	}

	deleteOrder(orderId: number) {
		const dialog = this.#dialog.open(ConfirmModalComponent, {
			data: {
				title: 'Delete Order',
				message: 'Are you sure you want to delete this order?'
			}
		});

		dialog.afterClosed().subscribe((result) => {
			if (result) {
				this.#graphQlService
					.postData(MutationQuery.DELETE_ORDER, { companyId: this.#appService.companyId, currentUserId: this.#appService.user.userId, orderId })
					.pipe(takeUntilDestroyed(this.#destroyRef))
					.subscribe((res) => {
						if (res.data['deleteOrder'].success) {
							this.getOrders(0);
							this.#utilitiesService.showSnackbar('Order deleted successfully');
						}
					});
			}
		});
	}

	//Prende e assegna i parametri dell'url
	private getParams(params: { [key: string]: string }): void {
		if (params['search']) this.orders.search = params['search'];

		if (params['photographers']) {
			const ids = params['photographers'].split('-').map((el) => new Ids(+el));
			this.getPhotographers(ids);
		} else {
			this.photographers.selected = [];
		}

		if (params['orderStatuses']) {
			const ids = params['orderStatuses'].split('-').map((el) => +el);
			this.getOrderStatus(ids);
		} else {
			this.orderStatuses.selected = [];
		}

		if (params['dueDate']) {
			const [start, end] = params['dueDate'].split('-');

			const toDate = (str: string) => {
				if (!str) return null;
				const [d, m, y] = str.split('/');
				return new Date(+y, +m - 1, +d);
			};

			this.dueDate.startDate = toDate(start);
			this.dueDate.endDate = toDate(end);
		}

		if (params['creationDate']) {
			const [start, end] = params['creationDate'].split('-');

			const toDate = (str: string) => {
				if (!str) return null;
				const [d, m, y] = str.split('/');
				return new Date(+y, +m - 1, +d);
			};

			this.creationDate.startDate = toDate(start);
			this.creationDate.endDate = toDate(end);
		}

		if (params['completitionDate']) {
			const [start, end] = params['completitionDate'].split('-');

			const toDate = (str: string) => {
				if (!str) return null;
				const [d, m, y] = str.split('/');
				return new Date(+y, +m - 1, +d);
			};

			this.completitionDate.startDate = toDate(start);
			this.completitionDate.endDate = toDate(end);
		}

		if (params['ids']) {
			const ids = params['ids'].split('-').map((el) => +el);
			this.ids.selected = ids;
		}
	}

	private getPhotographers(photographerIds: Array<Ids>) {
		const filters = {
			companyId: this.#appService.companyId,
			paginationPage: 0,
			paginationSize: 50,
			search: '',
			statusIds: [],
			photographerIds
		};

		this.#graphQlService
			.getData<Photographer>(PhotographerQueries.GET_PHOTOGRAPHERS, filters)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res) => res['data']['photographers'])
			)
			.subscribe((data: WrapperResultGraphQL<Photographer>) => {
				this['photographers'].selected = data.nodes.map((el) => ({
					...el,
					displayId: el.personId!,
					displayName: el.fullName!,
					selected: true,
					disabled: false
				}));
			});
	}

	getOrderStatus(ids: Array<number>) {
		this.#graphQlService
			.getData<any>(OrderQueries.GET_ORDER_STATUSES, { first: 50, after: null, search: '', ids })
			.pipe(
				map((res) => res['data']['orderStatuses']),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe({
				next: (data: WrapperResultGraphQL<any>) => {
					this.orderStatuses.selected = data.nodes.map((el) => ({
						statusId: el.id,
						statusName: el.name,
						displayId: el.id!,
						displayName: el.name,
						disabled: false,
						selected: true
					}));
				}
			});
	}

	applyFilters(filter: string, selected: Array<any>) {
		if (this[filter].selected) {
			if (!selected.length) {
				// Rimozione il parametro dall'url
				this.#filterService.removeDataFromUrl(filter, () => null);
			} else {
				this[filter].selected = selected;
				const ids = selected.map((el) => `${el.displayId}`).join('-');
				this.#filterService.setQueryParams({ [filter]: ids });
			}
		} else {
			const start = this[filter].startDate ? this[filter].startDate.toLocaleDateString() : null;
			const end = this[filter].endDate ? this[filter].endDate.toLocaleDateString() : null;
			const value = `${start}-${end}`;
			this.#filterService.setQueryParams({ [filter]: value });
		}
	}

	getOrders(page: number) {
		this.orders.page = page;
		const params = this.#filterService.getDataFromUrl();

		const dueDate = params && params['dueDate'] ? params['dueDate'].split('-').map((el) => el) : [];
		const creationDate = params && params['creationDate'] ? params['creationDate'].split('-').map((el) => el) : [];
		const completionDate = params && params['completionDate'] ? params['completionDate'].split('-').map((el) => el) : [];

		const filters = {
			ids: params && params['ids'] ? params['ids'].split('-').map((el) => +el) : [],
			photographerIds: params && params['photographers'] ? params['photographers'].split('-').map((el) => new Ids(+el)) : [],
			orderStatusIds: params && params['orderStatuses'] ? params['orderStatuses'].split('-').map((el) => new Ids(+el)) : [],

			startDueDate: dueDate[0] ?? null,
			endDueDate: dueDate[1] ?? null,

			startCreationDate: creationDate[0] ?? null,
			endCreationDate: creationDate[1] ?? null,

			startCompletitionDate: completionDate[0] ?? null,
			endCompletitionDate: completionDate[1] ?? null
		};
		const variables = {
			companyId: this.#appService.companyId,
			paginationPage: this.orders.page,
			paginationSize: this.orders.size,
			search: this.orders.search ?? '',
			filters
		};

		this.#graphQlService
			.getData<CompanyOrderListItem>(OrderQueries.GET_COMPANY_ORDERS, variables)
			.pipe(
				map((res) => res['data']['companyOrders']['nodes'][0]),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe({
				next: (data) => {
					this.orders.data = data.orders;
					this.orders.total = data.total;
					this.currentSidebar = null;
				}
			});
	}

	private queryParamsSubscribe() {
		this.#activatedRoute.queryParams.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((data) => {
			this.getOrders(0);
		});
	}
}
